import { useEffect, useRef, useState } from "react";

import { CloseOutlined } from "@ant-design/icons";
import { message } from "antd";
import capitalize from "lodash/capitalize";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { AppPagePaths } from "App/routes/paths";
import { AgentSettingsPaths } from "Pages/agentDesktop/Pages/AgentDesktopSettings/Pages/paths";
import { AgentDesktopPaths } from "Pages/agentDesktop/paths";
import { useFlags } from "Services/FeatureFlagService";
import { selectOnActiveCall } from "Services/state/agent/AgentInformationSlice";
import useEventListener from "Utils/useEventListener/useEventListener";

import { useGetAgentEmailIntegration } from "../useGetAgentEmailIntegration";

import styles from "./useEmailReauthWarning.module.scss";

const EMAIL_REAUTH_WARNING_MESSAGE_KEY = "EMAIL_REAUTH_WARNING_MESSAGE_KEY";

/**
 * Warns agents when they need to reauth their email integration.
 * - Shows an alert once every time the agent navigates to the agent desktop, or comes back to the agent desktop after focusing on a different browser tab/window
 * - If the agent is on an active call, waits to show the warning until after the call completes
 * - Clicking on the link in the warning skips the email Integration page and goes straight to email
 * - Does NOT warn if *just* the email integration status changes in Redux. There are separate error alerts for that, as it's typically in response to an agent action.
 */
export function useEmailReauthWarning(navigate: ReturnType<typeof useNavigate>) {
    const { email: emailVersion } = useFlags();
    const { integrationName, integration } = useGetAgentEmailIntegration();
    // We only want to show the warning to agents who are valid for email and had a once-successful integration that lost its connection
    const integrationConnectionLost = emailVersion > 0 && integration?.authorizationStatus === "DISCONNECTED";
    const agentIsOnActiveCall = useSelector(selectOnActiveCall);
    const documentRef = useRef(document);
    const { pathname } = useLocation();

    const [shouldWarnOnActiveCallEnd, setShouldWarnOnActiveCallEnd] = useState(false);

    const reconnectPath = `${AppPagePaths.agentDesktop}/${AgentDesktopPaths.mySettings}/${AgentSettingsPaths.integrations}`;
    const integrationTitle = capitalize(integrationName);

    const showWarning = () => {
        if (
            pathname !== AppPagePaths.agentDesktop &&
            pathname !== `${AppPagePaths.agentDesktop}/${AgentDesktopPaths.contacts[0]}`
        ) {
            return;
        }

        message.warn({
            content: (
                <div className={styles.customWarningContent}>
                    <div>
                        <p className={styles.titleText}>{integrationTitle} Integration Disconnected</p>
                        <p className={styles.bodyText}>
                            Your {integrationTitle} integration was disconnected. To receive email tasks,{" "}
                            <span className={styles.reconnectLink} onClick={() => navigate(reconnectPath)}>
                                reconnect
                            </span>
                            .
                        </p>
                    </div>
                    <CloseOutlined className={styles.cancelButton} />
                </div>
            ),
            duration: 0, // 0 means "do not auto-dismiss",
            key: EMAIL_REAUTH_WARNING_MESSAGE_KEY, // a consistent key guarantees that the same warning is never present on the screen multiple times at once
            className: styles.emailIntegrationWarningMessage,
            onClick: () => message.destroy(EMAIL_REAUTH_WARNING_MESSAGE_KEY),
        });
    };

    const handleWarningConditionsChange = () => {
        if (!integrationConnectionLost) {
            message.destroy(EMAIL_REAUTH_WARNING_MESSAGE_KEY);
            return;
        }
        if (agentIsOnActiveCall) {
            setShouldWarnOnActiveCallEnd(true);
            return;
        }
        showWarning();
    };

    // Listener for navigating back to regal app from other browser tab
    useEventListener("visibilitychange", handleWarningConditionsChange, documentRef);

    // Listener for navigating back to regal app from other windows (browser or otherwise)
    useEventListener("focus", handleWarningConditionsChange);

    // Listener to show delayed warnings if the warning would've happened during an active call
    useEffect(() => {
        if (agentIsOnActiveCall === false && shouldWarnOnActiveCallEnd === true) {
            handleWarningConditionsChange();
            setShouldWarnOnActiveCallEnd(false);
        }
    }, [agentIsOnActiveCall, shouldWarnOnActiveCallEnd]);

    // Listener for whenever we navigate to the agent desktop from another tab
    useEffect(() => {
        if (
            pathname === AppPagePaths.agentDesktop ||
            pathname === `${AppPagePaths.agentDesktop}/${AgentDesktopPaths.contacts[0]}`
        ) {
            handleWarningConditionsChange();
            return () => {
                message.destroy(EMAIL_REAUTH_WARNING_MESSAGE_KEY);
            };
        }
    }, [pathname]);
}
